import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CalendarClock } from "lucide-react";

export default function RescheduleModal({ open, onOpenChange, visit, onDone }) {
  const [form, setForm] = useState({ visit_date: "", start_time: "", end_time: "", location: "", notes: "" });
  const [applySeries, setApplySeries] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (visit && open) {
      setForm({
        visit_date: visit.visit_date || "",
        start_time: visit.start_time || "",
        end_time: visit.end_time || "",
        location: visit.location || "",
        notes: visit.notes || "",
      });
      setApplySeries(false);
    }
  }, [visit, open]);

  if (!visit) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (applySeries && visit.series_id) {
        const { data } = await api.put(`/visits/series/reschedule`, {
          anchor_visit_id: visit.id,
          visit_date: form.visit_date,
          start_time: form.start_time,
          end_time: form.end_time,
          location: form.location || null,
        });
        toast.success(`${data.shifted} visits in the series rescheduled — pending re-approval`);
      } else {
        await api.put(`/visits/${visit.id}/reschedule`, form);
        toast.success("Visit rescheduled — pending re-approval");
      }
      onDone?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="reschedule-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <CalendarClock className="h-5 w-5 text-[#0d9488]" /> Reschedule Visit
          </DialogTitle>
          <DialogDescription>
            Visit with {visit.child_name}. Rescheduling resets it to Pending for re-approval.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Date</Label>
            <Input type="date" data-testid="reschedule-date-input" value={form.visit_date} onChange={set("visit_date")} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Start time</Label>
              <Input type="time" data-testid="reschedule-start-input" value={form.start_time} onChange={set("start_time")} required />
            </div>
            <div className="space-y-1.5">
              <Label>End time</Label>
              <Input type="time" data-testid="reschedule-end-input" value={form.end_time} onChange={set("end_time")} required />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Location</Label>
            <Input data-testid="reschedule-location-input" value={form.location} onChange={set("location")} placeholder="Family Center, Room 3" />
          </div>
          {visit.series_id && (
            <label className="flex items-start gap-3 rounded-lg border border-indigo-200 bg-indigo-50/60 p-3" data-testid="apply-series-toggle">
              <Checkbox checked={applySeries} onCheckedChange={(c) => setApplySeries(!!c)} data-testid="apply-series-check" className="mt-0.5" />
              <div>
                <p className="text-sm font-medium text-slate-800">Apply to entire recurring series</p>
                <p className="text-xs text-slate-500">Shifts every upcoming visit in this series by the same amount, keeping the cadence. All will need re-approval.</p>
              </div>
            </label>
          )}
          {!applySeries && (
            <div className="space-y-1.5">
              <Label>Notes (optional)</Label>
              <Textarea data-testid="reschedule-notes-input" value={form.notes} onChange={set("notes")} rows={2} />
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" data-testid="submit-reschedule-btn" disabled={loading} className="bg-[#1e3a8a] hover:bg-[#172554]">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {applySeries ? "Reschedule Series" : "Reschedule"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
